import React from "react";
import {Box, Checkbox, Chip, Colors, Container, Text} from "@bounce/shared/ui";
import {MenuItem, Select, TextField} from "../../../ui/components";
import {CHANNEL_DISPLAY} from "../../templates/templateDisplay";
import {PromoDayLabel} from "./dates";
import {INPUT_SX} from "./FormCard";
import {
  DayAction,
  DEFAULT_REMINDER_DELAY_HOURS,
  FORCE_DAY_ACTION,
  MODEL_DAY_ACTION,
  NO_SEND_DAY_ACTION,
  parseNumberInput,
  ReminderFormValues,
} from "./helpers";
import {PromoTemplateOption} from "./PromoTemplatesSelect";

const ACTION_SELECT_WIDTH = 280;
const DELAY_INPUT_WIDTH = 90;
const MODEL_LABEL = "Model chooses";
const NO_SEND_LABEL = "Send nothing";
const FORCE_LABEL = "Force (model picks the channel)";
const REMINDER_LABEL = "Reminder";
const NO_REMINDER_LABEL = "No reminder";
const REMINDER_ON_LABEL = "Remind on the other channel after";
const HOURS_LABEL = "hours";
const NO_REMINDER = "none";
const REMINDER_ON = "on";
const TEMPLATES_LABEL = "Templates";
const PICK_TEMPLATES_LABEL = "Email and SMS (MMS optional)";

const SELECT_ALL = "select_all";
const SELECT_ALL_LABEL = "Select all";

const REMINDER_HINT = "Email is followed by the SMS; SMS or MMS is followed by the email.";

const dayLabelText = (label: PromoDayLabel) => (label.detail ? `${label.title} · ${label.detail}` : label.title);

const templateLabel = (template: PromoTemplateOption | undefined, templateId: number) =>
  template ? `${template.name} (${CHANNEL_DISPLAY[template.content_channel].label})` : `Template ${templateId}`;

type Props = {
  dayLabels: PromoDayLabel[];
  templateIds: number[];
  templateById: Map<number, PromoTemplateOption>;
  actionForDay: (day: number) => DayAction;
  onChange: (day: number, action: DayAction) => void;
  forcedTemplatesFor: (day: number) => number[];
  onForcedTemplatesChange: (day: number, templateIds: number[]) => void;
  reminderForDay: (day: number) => ReminderFormValues | undefined;
  onReminderChange: (day: number, reminder: ReminderFormValues | null) => void;
};

const LabeledRow = ({label, children}: {label: string; children: React.ReactNode}) => (
  <Container row fullWidth sx={{gap: 2, alignItems: "center", justifyContent: "flex-end"}}>
    <Text size={13} sx={{color: Colors.grey._500}}>
      {label}
    </Text>
    {children}
  </Container>
);

type ForceRowsProps = Pick<
  Props,
  | "templateIds"
  | "templateById"
  | "forcedTemplatesFor"
  | "onForcedTemplatesChange"
  | "reminderForDay"
  | "onReminderChange"
> & {day: number};

const ForceRows = ({
  day,
  templateIds,
  templateById,
  forcedTemplatesFor,
  onForcedTemplatesChange,
  reminderForDay,
  onReminderChange,
}: ForceRowsProps) => {
  const reminder = reminderForDay(day);
  const forcedTemplateIds = forcedTemplatesFor(day);
  const allTemplatesForced = templateIds.every((templateId) => forcedTemplateIds.includes(templateId));

  const onTemplatesSelect = (selected: (number | typeof SELECT_ALL)[]) => {
    if (selected.includes(SELECT_ALL)) {
      onForcedTemplatesChange(day, allTemplatesForced ? [] : templateIds);
      return;
    }
    onForcedTemplatesChange(day, selected as number[]);
  };

  return (
    <>
      <LabeledRow label={TEMPLATES_LABEL}>
        <Box sx={{width: ACTION_SELECT_WIDTH}}>
          <Select
            multiple
            fullWidth
            size="small"
            displayEmpty
            value={forcedTemplateIds}
            onChange={(event) => onTemplatesSelect(event.target.value as (number | typeof SELECT_ALL)[])}
            renderValue={(selected) =>
              (selected as number[]).length ? (
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                  {(selected as number[]).map((templateId) => (
                    <Chip
                      key={templateId}
                      size_="small"
                      variant_="blue"
                      label={templateLabel(templateById.get(templateId), templateId)}
                    />
                  ))}
                </Box>
              ) : (
                <Text size={14} sx={{color: Colors.grey._500}}>
                  {PICK_TEMPLATES_LABEL}
                </Text>
              )
            }
            error={forcedTemplateIds.length === 0}
            sx={INPUT_SX}
          >
            {templateIds.length > 1 && (
              <MenuItem value={SELECT_ALL}>
                <Checkbox checked={allTemplatesForced} />
                {SELECT_ALL_LABEL}
              </MenuItem>
            )}
            {templateIds.map((templateId) => (
              <MenuItem key={templateId} value={templateId}>
                <Checkbox checked={forcedTemplateIds.includes(templateId)} />
                {templateLabel(templateById.get(templateId), templateId)}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </LabeledRow>
      <LabeledRow label={REMINDER_LABEL}>
        {reminder && (
          <Container row sx={{gap: 1, alignItems: "center"}}>
            <TextField
              type="number"
              size="small"
              value={reminder.delay_hours ?? ""}
              onChange={(event) =>
                onReminderChange(day, {...reminder, delay_hours: parseNumberInput(event.target.value)})
              }
              error={reminder.delay_hours === null || reminder.delay_hours < 1}
              sx={{...INPUT_SX, width: DELAY_INPUT_WIDTH}}
            />
            <Text size={13} sx={{color: Colors.grey._500}}>
              {HOURS_LABEL}
            </Text>
          </Container>
        )}
        <Box sx={{width: ACTION_SELECT_WIDTH}}>
          <Select
            fullWidth
            size="small"
            value={reminder ? REMINDER_ON : NO_REMINDER}
            onChange={(event) =>
              onReminderChange(
                day,
                event.target.value === REMINDER_ON ? {day, delay_hours: DEFAULT_REMINDER_DELAY_HOURS} : null
              )
            }
            sx={INPUT_SX}
          >
            <MenuItem value={NO_REMINDER}>{NO_REMINDER_LABEL}</MenuItem>
            <MenuItem value={REMINDER_ON}>{REMINDER_ON_LABEL}</MenuItem>
          </Select>
        </Box>
      </LabeledRow>
      {reminder && (
        <Text size={12} sx={{color: Colors.grey._500, textAlign: "right"}}>
          {REMINDER_HINT}
        </Text>
      )}
    </>
  );
};

const PromoDayPlan = ({dayLabels, actionForDay, onChange, ...forceRowsProps}: Props) => (
  <Container fullWidth sx={{gap: 1}}>
    {dayLabels.map((label, day) => {
      const action = actionForDay(day);
      return (
        <Container
          key={day}
          fullWidth
          sx={{
            gap: 1,
            p: "8px 14px",
            borderRadius: "10px",
            border: `1px solid ${Colors.tertiary._300}`,
            backgroundColor: Colors.bw.white,
          }}
        >
          <Container row fullWidth sx={{gap: 2, alignItems: "center", justifyContent: "space-between"}}>
            <Text size={14} variant="inter600" sx={{color: Colors.grey._900}}>
              {dayLabelText(label)}
            </Text>
            <Box sx={{width: ACTION_SELECT_WIDTH}}>
              <Select
                fullWidth
                size="small"
                value={action}
                onChange={(event) => onChange(day, event.target.value as DayAction)}
                sx={INPUT_SX}
              >
                <MenuItem value={MODEL_DAY_ACTION}>{MODEL_LABEL}</MenuItem>
                <MenuItem value={NO_SEND_DAY_ACTION}>{NO_SEND_LABEL}</MenuItem>
                <MenuItem value={FORCE_DAY_ACTION}>{FORCE_LABEL}</MenuItem>
              </Select>
            </Box>
          </Container>
          {action === FORCE_DAY_ACTION && <ForceRows day={day} {...forceRowsProps} />}
        </Container>
      );
    })}
  </Container>
);

export default PromoDayPlan;
